import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSpinner, FaCalendarAlt, FaMapMarkerAlt, FaImages, FaStar } from 'react-icons/fa';
import api from '../../api/axiosConfig';
import './all-events.css';

const AllEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchEvents();
  }, [filter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError('');

      const postsResponse = await api.get('/posts', { 
        params: { 
          status: 1
        } 
      });

      if (postsResponse.data.success) {
        const posts = postsResponse.data.data || [];
        
        const eventsWithPictures = await Promise.all(
          posts.map(async (post) => {
            try {
              const picturesResponse = await api.get('/pictures', {
                params: {
                  post_id: post.id
                }
              });
              
              let images = [];
              if (picturesResponse.data.success) {
                // CLIENT-SIDE FILTERING: Only use images that belong to this post
                images = picturesResponse.data.data.filter(image => 
                  image.post_id === post.id
                );
              }
              
              return {
                ...post,
                images: images,
                hasImages: images.length > 0
              };
            } catch (pictureError) {
              return {
                ...post,
                images: [],
                hasImages: false
              };
            }
          })
        );

        setEvents(eventsWithPictures);
      } else {
        setError(postsResponse.data.message || 'Failed to load events');
      }
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message.includes('Network Error')) {
        setError('Cannot connect to the server. Please try again later.');
      } else {
        setError('Unable to load events. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBA';
    
    try {
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      };
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (error) {
      return 'Date TBA';
    }
  };

  const getEventImage = (event) => {
    if (event.images && event.images.length > 0) {
      const image = event.images[0];
      let imageUrl = image.picture_path || image.url || image.path;
      
      if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
        imageUrl = `/${imageUrl}`;
      }
      
      if (imageUrl && imageUrl.startsWith('/') && !imageUrl.startsWith('http')) {
        imageUrl = `http://192.168.100.101:8000${imageUrl}`;
      }
      
      return imageUrl;
    }
    return '/images/event-placeholder.jpg';
  };

  const truncateText = (htmlText, maxLength) => {
    if (!htmlText) return 'No description available';
    const plainText = htmlText.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    if (plainText.length <= maxLength) return plainText;
    return plainText.substr(0, maxLength) + '...';
  };

  const getFilteredEvents = () => {
    const now = new Date();
    return events.filter(event => {
      if (!event.event_date) return filter === 'all';
      try {
        const eventDate = new Date(event.event_date);
        if (isNaN(eventDate.getTime())) return filter === 'all';
        switch (filter) {
          case 'upcoming':
            return eventDate >= now;
          case 'past':
            return eventDate < now;
          default:
            return true;
        }
      } catch (error) {
        return filter === 'all';
      }
    });
  };

  const filteredEvents = getFilteredEvents();

  const eventCounts = {
    all: events.length,
    upcoming: events.filter(e => {
      try {
        return e.event_date && new Date(e.event_date) >= new Date();
      } catch {
        return false;
      }
    }).length,
    past: events.filter(e => {
      try {
        return e.event_date && new Date(e.event_date) < new Date();
      } catch {
        return false;
      }
    }).length
  };

  if (loading) {
    return (
      <section className="events-listing">
        <div className="container">
          <div className="support-section-title">
            <h2>Our Events & Programs</h2>
            <p>Transforming lives through meaningful events and gatherings</p>
          </div>
          <div className="loading-container">
            <FaSpinner className="spinner" />
            <p>Loading events from ITAMBUE THAMANI YAKO...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="events-listing">
        <div className="container">
          <div className="support-section-title">
            <h2>Our Events & Programs</h2>
            <p className="error-text">{error}</p>
          </div>
          <div className="events-error">
            <button onClick={fetchEvents} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="events-listing">
      <div className="container">
        <div className="support-section-title">
          <h2>Our Events & Programs</h2>
          <p>Join us in our mission to empower communities through education and sustainable development</p>
        </div>

        <div className="events-filter">
          <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            All Events ({eventCounts.all})
          </button>
          <button className={`filter-btn ${filter === 'upcoming' ? 'active' : ''}`} onClick={() => setFilter('upcoming')}>
            Upcoming ({eventCounts.upcoming})
          </button>
          <button className={`filter-btn ${filter === 'past' ? 'active' : ''}`} onClick={() => setFilter('past')}>
            Past Events ({eventCounts.past})
          </button>
        </div>

        {filteredEvents.length === 0 ? (
          <div className="no-events">
            <div className="no-events-icon">
              <FaImages />
            </div>
            <h3>No events found</h3>
            <p>There are no {filter !== 'all' ? filter : ''} events to display at the moment.</p>
            <button onClick={fetchEvents} className="retry-btn">
              Refresh Events
            </button>
          </div>
        ) : (
          <>
            <div className="events-grid">
              {filteredEvents.map(event => (
                <div className="event-card" key={event.id}>
                  <div className="event-image">
                    <img 
                      src={getEventImage(event)} 
                      alt={event.title}
                      onError={(e) => {
                        e.target.src = '/images/event-placeholder.jpg';
                      }}
                      loading="lazy"
                    />
                    {event.featured && (
                      <span className="featured-badge">
                        <FaStar className="featured-icon" />
                        Featured
                      </span>
                    )}
                    {!event.hasImages && (
                      <div className="no-image-placeholder">
                        <FaImages className="placeholder-icon" />
                        <span>No Images</span>
                      </div>
                    )}
                    {event.images && event.images.length > 0 && (
                      <div className="image-count-overlay">
                        <FaImages />
                        {event.images.length}
                      </div>
                    )}
                  </div>
                  
                  <div className="event-content">
                    <div className="event-meta-all-events">
                      <span className="event-date">
                        <FaCalendarAlt className="meta-icon" />
                        {formatDate(event.event_date)}
                      </span>
                      {event.category && (
                        <span className="event-category">{event.category}</span>
                      )}
                    </div>
                    
                    <h3 className="event-title-all">{event.title}</h3>
                    
                    <div className="event-description">
                      {truncateText(event.post, 120)}
                    </div>

                    <div className="event-footer">
                      {event.location && (
                        <div className="event-location">
                          <FaMapMarkerAlt className="location-icon" />
                          {event.location}
                        </div>
                      )}
                      <Link 
                        to={`/event-details/${event.id}`}
                        className="event-link"
                      >
                        Read More
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="events-summary">
              <div className="summary-content">
                <p className="summary-text">
                  Showing <span className="highlight">{filteredEvents.length}</span> of <span className="highlight">{events.length}</span> events
                  {filter !== 'all' && <span className="filter-indicator"> (filtered by {filter})</span>}
                </p>
                <div className="summary-actions">
                  <button onClick={fetchEvents} className="refresh-btn">
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default AllEvents;